<template>
  <div class="wheel-fortune">
    <div class="wheel-fortune__segments" :style="`background: url('${image}') center / cover no-repeat`"></div>
    <div class="wheel-fortune__blackout"></div>
    <div class="wheel-fortune__frame"></div>
    <button class="wheel-fortune__button" type="button" :disabled="spinning">
      <span class="spin-wheel">КРУТИТЬ</span>
      <span class="spin-again"></span>
    </button>
  </div>
</template>

<script>
import Vue from "vue"
import WheelFortune from "wheel-fortune"
import { OFFLINE_WHEEL_FORTUNE_PRIZE_MAP, OFFLINE_WHEEL_FORTUNE_PRIZES } from "@pages/axoft-wheel-fortune/consts/wheelFortune";

export default {
  name: "amp-wheel-fortune",
  emits: ["win"],
  props: {
    image: String,
  },
  data() {
    return {
      wheel: null,
      spinning: false,
      available: [],
      prizes: [
        { key: OFFLINE_WHEEL_FORTUNE_PRIZE_MAP.secretPrize, label: "Суперсекретный приз", weight: 5, limit: 0 },
        { key: OFFLINE_WHEEL_FORTUNE_PRIZE_MAP.simpleWineGiftCertificate, label: "Подарочный сертификат SimpleWine", weight: 10, limit: 0 },
        { key: OFFLINE_WHEEL_FORTUNE_PRIZE_MAP.souvenirFlant, label: "Сувенирка Флант", weight: 5, limit: 0 },
        { key: OFFLINE_WHEEL_FORTUNE_PRIZE_MAP.souvenirPhishman, label: "Сувенирка Phishman", weight: 5, limit: 0 },
        { key: OFFLINE_WHEEL_FORTUNE_PRIZE_MAP.yaPlus, label: "Подписка Яндекс. Плюс", weight: 5, limit: 0 },
        { key: OFFLINE_WHEEL_FORTUNE_PRIZE_MAP.souvenirAstraGroup, label: "Сувенирка Группа Астра", weight: 5, limit: 0 },
        { key: OFFLINE_WHEEL_FORTUNE_PRIZE_MAP.paperStickerPack, label: "Бумажный стикер-пак", weight: 15, limit: 0 },
        { key: OFFLINE_WHEEL_FORTUNE_PRIZE_MAP.tgStickerPack, label: "Электронный стикерпак для ТГ", weight: 50, limit: Infinity },
      ]
    }
  },
  created() {
    this.refreshPrizeLimits()
  },
  computed: {
    prizeByKey() {
      const map = {}
      this.prizes.forEach(p => (map[p.key] = p))
      return map
    },
    OFFLINE_WHEEL_FORTUNE_PRIZE_MAP() {
      return OFFLINE_WHEEL_FORTUNE_PRIZE_MAP
    },
    OFFLINE_WHEEL_FORTUNE_PRIZES() {
      return OFFLINE_WHEEL_FORTUNE_PRIZES
    }
  },
  mounted() {
    this.initWheelWithState({ targetAngle: this.centerAngleForKey(this.OFFLINE_WHEEL_FORTUNE_PRIZE_MAP.secretPrize) })
    const btn = document.querySelector(".wheel-fortune__button")
    btn.addEventListener("click", this.handleSpinClick, { capture: true })
  },
  beforeDestroy() {
    const btn = document.querySelector(".wheel-fortune__button")
    if (btn) btn.removeEventListener("click", this.handleSpinClick, { capture: true })
    if (this.wheel?.destroy) this.wheel.destroy()
  },
  methods: {
    async refreshPrizeLimits() {
      const prizeList = await this.fetchPrizeList()
      prizeList.forEach(actPrize => {
        this.prizes.forEach(prize => {
          if (actPrize.slug === prize.key) {
            prize.limit = actPrize.limit ?? Infinity
          }
        })
      })
    },
    async fetchPrizeList() {
      try {
        const response = await Vue.http.get(`/api/events/getPrizeList/offline`)

        if (response.status === 200) {
          // limit, slug, title
          return response.body.data
        } else return []
      } catch (e) {
        console.error(e)
        return []
      }
    },
    checkAvailable() {
      this.available = this.prizes.filter(p => {
        return p.limit > 0
      })
    },
    initWheelWithState(state) {
      if (this.wheel?.destroy) this.wheel.destroy()
      this.wheel = new WheelFortune({
        rootSelector: ".wheel-fortune",
        wheelSelector: ".wheel-fortune__segments",
        triggerSelector: ".wheel-fortune__button",
        rotationCount: 6,
        duration: 4800,
        overshootDeg: 12,
        returnDuration: 1200,
        spinStates: [state],
      })
      this.wheel.init()
    },

    handleSpinClick(e) {
      if (this.spinning) {
        e.stopImmediatePropagation?.()
        e.preventDefault()
        return
      }

      this.checkAvailable()

      const winnerKey = this.pickPrizeKey()
      const angle = this.centerAngleForKey(winnerKey)
      this.initWheelWithState({
        targetAngle: angle,
        callback: () => this.onSpinEnd(winnerKey),
      })

      this.spinning = true
    },

    onSpinEnd(winnerKey) {
      const p = this.prizeByKey[winnerKey]
      this.spinning = false
      this.$emit('win', winnerKey)
    },
    pickPrizeKey() {
      const avail = this.available
      if (avail.length === 0) return this.OFFLINE_WHEEL_FORTUNE_PRIZE_MAP.tgStickerPack

      const total = avail.reduce((s, it) => s + it.weight, 0)
      let r = Math.random() * total
      for (const it of avail) {
        r -= it.weight
        if (r <= 0) return it.key
      }
      return avail[avail.length - 1].key
    },
    indicesForKey(key) {
      const idxs = []
      this.OFFLINE_WHEEL_FORTUNE_PRIZES.forEach((k, i) => {
        if (k === key) idxs.push(i + 1)
      })
      return idxs
    },
    centerAngleForKey(key) {
      const idxs = this.indicesForKey(key)
      if (!idxs.length) {
        console.warn(`На картинке нет сегмента для ключа ${key}. Проверьте список призов.`)
        return 0
      }

      const idx = idxs[Math.floor(Math.random() * idxs.length)]
      const N = this.OFFLINE_WHEEL_FORTUNE_PRIZES.length
      const sector = 360 / N

      return sector - idx * sector
    },
  },
}
</script>

<style lang="sass">
@import "~@src/sass/params"

@mixin wheel-center($inset...)
  position: absolute
  inset: $inset
  margin: auto

.wheel-fortune
  --wheel-total-size: min(100vmin - 40px, 520px)
  --wheel-decrease-segments: 1%
  --wheel-decrease-blackout: 1%
  --wheel-decrease-blurring: 1%
  --wheel-decrease-frame: 0
  --wheel-decrease-button: 33%
  --wheel-blackout-color: rgb(10 10 10)

  position: relative
  width: var(--wheel-total-size)
  height: var(--wheel-total-size)
  overflow: hidden
  border-radius: 100%

  &__segments
    @include wheel-center(var(--wheel-decrease-segments))
    filter: blur(var(--blurring))

  &__blackout
    --cutout: calc(100% - (100% / 8))
    @include wheel-center(var(--wheel-decrease-blackout))
    border-radius: 100%
    opacity: 0
    background: conic-gradient(from 0deg, var(--wheel-blackout-color) var(--cutout), transparent var(--cutout))
    transform: rotate(calc((360deg / 8) / 2))
    .wheel-fortune--completed &
      animation: blackout-fade 2s ease forwards
    @keyframes blackout-fade
      0%
        opacity: 0
      25%
        opacity: .65
      75%
        opacity: .65
      100%
        opacity: 0

  &__frame
    @include wheel-center(var(--wheel-decrease-frame))
    pointer-events: none
    box-shadow: 0 0 0 calc(var(--wheel-total-size) / 30) inset Black
    border-radius: 100%

  &__button
    @include wheel-center(var(--wheel-decrease-button))
    --opacity-spin-wheel: 1
    --opacity-spin-again: 0
    background-color: Black
    color: WhiteSmoke
    cursor: pointer
    border-radius: 100%
    transition: filter 425ms ease-in-out

    .spin-wheel,
    .spin-again
      position: absolute
      inset: 0
      margin: auto
      display: grid
      place-content: center
      text-align: center
      text-transform: uppercase
      font-weight: 600
      font-size: clamp(16px, 5cqw, 36px)
      color: #FFFFFF
      transition: opacity 725ms ease-in-out

    .spin-wheel
      opacity: var(--opacity-spin-wheel)

    .spin-again
      opacity: var(--opacity-spin-again)

    .wheel-fortune--spinning &,
    .wheel-fortune--final-done &
      pointer-events: none
      filter: grayscale(.4) brightness(.4)

    .wheel-fortune--first-done &
      --opacity-spin-wheel: 0
      --opacity-spin-again: 1

.wheel-fortune:not(.wheel-fortune--first-done) .spin-again
  visibility: hidden
</style>



// WEBPACK FOOTER //
// src/templates/pages/axoft-wheel-fortune/components/amp-wheel-fortune.vue